<script setup lang="ts">
import {
  useElementSize,
  useIntersectionObserver,
  usePreferredReducedMotion,
  useRafFn
} from '@vueuse/core'

// Decorative background: drifting dots linked by a line when close, fading with
// distance. Coordinates are in CSS pixels and the viewBox tracks the host's real
// size, so one unit is the same length on both axes — a fixed square viewBox
// with preserveAspectRatio="none" skewed motion and squashed the dots.
const PARTICLE_COUNT = 6
const LINK_DISTANCE = 180
const SPEED = 22 // px per second

// Narrow hosts get half the dots and half the link radius: the full-size values
// were tuned against a ~320px wide block and turn into a dense mesh in a
// phone-width strip. Keyed off measured width, not a media query, so a cramped
// container on desktop gets the same treatment.
const NARROW_WIDTH = 640

// How far past the edge a dot drifts before it counts as having left.
const WRAP_MARGIN = 12

// A dot that leaves stays away for a random spell before re-entering from the
// opposite side, so the crossing reads as drift rather than a hard wrap.
const RESPAWN_MIN_MS = 600
const RESPAWN_MAX_MS = 2600

interface Particle {
  // Stable across the dot's life: the visible list is filtered, so index keys
  // would shuffle DOM nodes between dots on every wrap.
  id: number
  x: number
  y: number
  vx: number
  vy: number
  r: number
  // performance.now timestamp at which an off-screen dot re-enters; null while
  // the dot is in play.
  respawnAt: number | null
}

// Measured on the host element rather than the svg: the svg is absolutely
// positioned, so measuring it would feed its own size back into the viewBox.
const root = ref<SVGSVGElement | null>(null)
const host = computed(() => root.value?.parentElement ?? null)
const { width: hostWidth, height: hostHeight } = useElementSize(host)

// Falls back to the typical block size until measured, so the first frame lays
// dots out over a plausible area rather than a zero-sized one.
const box = computed(() => ({
  width: hostWidth.value || 320,
  height: hostHeight.value || 70
}))

// Rounded so an odd particle count can't produce a fractional length for
// slice()/Array.from().
const scale = computed(() => (box.value.width < NARROW_WIDTH ? 0.5 : 1))
const particleCount = computed(() => Math.round(PARTICLE_COUNT * scale.value))
const linkDistance = computed(() => LINK_DISTANCE * scale.value)

let particleId = 0

function makeParticle(): Particle {
  const angle = Math.random() * Math.PI * 2
  return {
    id: particleId++,
    x: Math.random() * box.value.width,
    y: Math.random() * box.value.height,
    vx: Math.cos(angle) * SPEED,
    vy: Math.sin(angle) * SPEED,
    r: 2.5 + Math.random() * 0.8,
    respawnAt: null
  }
}

function respawnDelay(): number {
  return RESPAWN_MIN_MS + Math.random() * (RESPAWN_MAX_MS - RESPAWN_MIN_MS)
}

// Re-enters the dot just outside the edge opposite the one it left through. The
// entry axis gets a fixed inward velocity; the other is randomised so dots
// don't all cross on the same track.
function reenter(p: Particle) {
  const { width, height } = box.value
  const drift = (Math.random() - 0.5) * SPEED

  if (p.x < 0) {
    p.x = width + WRAP_MARGIN
    p.y = Math.random() * height
    p.vx = -SPEED
    p.vy = drift
  } else if (p.x > width) {
    p.x = -WRAP_MARGIN
    p.y = Math.random() * height
    p.vx = SPEED
    p.vy = drift
  } else if (p.y < 0) {
    p.y = height + WRAP_MARGIN
    p.x = Math.random() * width
    p.vy = -SPEED
    p.vx = drift
  } else {
    p.y = -WRAP_MARGIN
    p.x = Math.random() * width
    p.vy = SPEED
    p.vx = drift
  }
}

// shallowRef: step() mutates particle fields every frame, and deep reactivity
// would fire a notification per field per dot. Signalled via triggerRef once
// per frame instead.
const particles = shallowRef<Particle[]>([])

// Dots waiting to re-enter must not be drawn or linked while they wait.
const visibleParticles = computed(() => particles.value.filter((p) => p.respawnAt === null))

const links = computed(() => {
  const result: { x1: number; y1: number; x2: number; y2: number; opacity: number }[] = []
  const items = visibleParticles.value
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i]
      const b = items[j]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      if (dist < linkDistance.value) {
        result.push({
          x1: a.x,
          y1: a.y,
          x2: b.x,
          y2: b.y,
          opacity: 1 - dist / linkDistance.value
        })
      }
    }
  }
  return result
})

let lastFrame = 0

function step() {
  const timestamp = performance.now()
  // Delta-timed so the drift speed does not depend on the display refresh rate.
  const delta = lastFrame ? Math.min((timestamp - lastFrame) / 1000, 0.1) : 0
  lastFrame = timestamp

  const { width, height } = box.value
  for (const p of particles.value) {
    if (p.respawnAt !== null) {
      if (timestamp >= p.respawnAt) {
        reenter(p)
        p.respawnAt = null
      }
      continue
    }

    p.x += p.vx * delta
    p.y += p.vy * delta

    const isOutside =
      p.x < -WRAP_MARGIN ||
      p.x > width + WRAP_MARGIN ||
      p.y < -WRAP_MARGIN ||
      p.y > height + WRAP_MARGIN

    if (isOutside) p.respawnAt = timestamp + respawnDelay()
  }

  triggerRef(particles)
}

// useRafFn tears the loop down with the component scope, so there is no manual
// cancelAnimationFrame to keep in sync with unmount.
const { pause: pauseLoop, resume: resumeLoop } = useRafFn(step, { immediate: false })

// Decoration only: off for reduced-motion readers, and paused off-screen —
// otherwise it animates for as long as the article is open.
const reducedMotion = usePreferredReducedMotion()
const isOnScreen = ref(false)
useIntersectionObserver(root, ([entry]) => {
  isOnScreen.value = entry?.isIntersecting ?? false
})

watch(
  [isOnScreen, reducedMotion],
  ([onScreen, motion]) => {
    if (onScreen && motion !== 'reduce') {
      lastFrame = 0
      resumeLoop()
    } else {
      pauseLoop()
    }
  },
  { immediate: true }
)

// Resyncs the dot count when the host crosses the narrow threshold without
// respawning the existing dots.
watch(
  particleCount,
  (count) => {
    const current = particles.value
    if (current.length > count) {
      particles.value = current.slice(0, count)
    } else if (current.length < count) {
      particles.value = [
        ...current,
        ...Array.from({ length: count - current.length }, makeParticle)
      ]
    }
  },
  { immediate: true }
)
</script>

<template>
  <!-- Motion and link opacity are computed in JS (see particles / links) rather
       than CSS keyframes: dots connect only when close to each other, which
       looping animations can't express. -->
  <svg
    ref="root"
    class="pointer-events-none absolute inset-0 h-full w-full"
    aria-hidden="true"
    :viewBox="`0 0 ${box.width} ${box.height}`"
  >
    <line
      v-for="(link, i) in links"
      :key="i"
      class="airun-connection-line"
      fill="none"
      :x1="link.x1"
      :y1="link.y1"
      :x2="link.x2"
      :y2="link.y2"
      :style="{ strokeOpacity: link.opacity }"
    />
    <circle
      v-for="p in visibleParticles"
      :key="p.id"
      class="airun-particle"
      :cx="p.x"
      :cy="p.y"
      :r="p.r"
    />
  </svg>
</template>

<style scoped>
.airun-connection-line {
  stroke: rgba(147, 174, 255, 0.85);
  stroke-width: 1;
  stroke-linecap: round;
}

.airun-particle {
  fill: rgba(230, 238, 255, 0.95);
  filter: drop-shadow(0 0 3px rgba(180, 200, 255, 0.9));
}
</style>
